import React from 'react';
import styled from 'styled-components';
import {Detail, Icon, Pill, PillVariant, Text} from '@forecast-it/design-system';
import {FeatureFlagRowData} from './featureFlagRows';
import {getTeam, UNMAPPED_TEAM_COLOUR} from './featureFlagTeams';
import {
	FEATURE_FLAG_GRID_COLUMNS,
	FEATURE_FLAG_GRID_GAP,
	FEATURE_FLAG_GRID_PADDING_LEFT,
	FEATURE_FLAG_GRID_PADDING_RIGHT,
} from './featureFlagGrid';

const RowCard = styled.div<{$interactive: boolean}>`
	position: relative;
	display: grid;
	grid-template-columns: ${FEATURE_FLAG_GRID_COLUMNS};
	align-items: center;
	gap: ${FEATURE_FLAG_GRID_GAP};
	min-height: 56px;
	padding: 8px ${FEATURE_FLAG_GRID_PADDING_RIGHT} 8px ${FEATURE_FLAG_GRID_PADDING_LEFT};
	background: #ffffff;
	border: 1px solid #d3d3df;
	border-radius: 4px;
	box-shadow: 0 1px 2px rgba(31, 31, 91, 0.08);
	overflow: hidden;
	cursor: ${props => (props.$interactive ? 'pointer' : 'default')};

	&:hover {
		border-color: ${props => (props.$interactive ? '#0004db' : '#d3d3df')};
		background: ${props => (props.$interactive ? '#fcfbfd' : '#ffffff')};
	}
`;

const AccentBar = styled.span<{$colour: string}>`
	position: absolute;
	left: 0;
	top: 0;
	bottom: 0;
	width: 4px;
	background: ${props => props.$colour};
`;

const FlagName = styled.span`
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

const TeamCell = styled.span`
	display: flex;
	align-items: center;
	gap: 8px;
`;

const TeamSwatch = styled.span<{$colour: string}>`
	width: 10px;
	height: 10px;
	border-radius: 2px;
	flex: none;
	background: ${props => props.$colour};
`;

const NumberCell = styled.span`
	text-align: right;
	font-variant-numeric: tabular-nums;
`;

const RolloutCell = styled.span`
	display: flex;
	flex-direction: column;
	gap: 3px;
	align-items: flex-start;
`;

const ProgressRow = styled.span`
	display: flex;
	align-items: center;
	gap: 10px;
	width: 100%;
`;

const ProgressTrack = styled.span`
	flex: 1;
	height: 6px;
	border-radius: 999px;
	background: #e6e6ed;
	overflow: hidden;
`;

const ProgressFill = styled.span<{$percent: number}>`
	display: block;
	height: 100%;
	background: #0004db;
	width: ${props => props.$percent}%;
`;

const PercentLabel = styled.span`
	flex: none;
	width: 38px;
	text-align: right;
	font-variant-numeric: tabular-nums;
`;

const CleanedUpLine = styled.span`
	display: flex;
	align-items: center;
	gap: 6px;
	padding-left: 8px;
`;

const ChevronCell = styled.span`
	display: flex;
	justify-content: flex-end;
`;

export interface FeatureFlagRowProps {
	row: FeatureFlagRowData;
	/**
	 * Opens the flag's detail view. Rolled-out flags are never interactive — they are configured in
	 * code, so there is nothing to open.
	 */
	onOpen?: (key: string) => void;
}

export function FeatureFlagRow({row, onOpen}: FeatureFlagRowProps): JSX.Element {
	// A team the map does not know yet still gets a row — drawn in neutral grey, under whatever name
	// the backend gave it (see teamLabel in featureFlagRows).
	const teamColour = row.teamId ? getTeam(row.teamId).colour : UNMAPPED_TEAM_COLOUR;
	const interactive = !row.isRolledOut;

	return (
		<RowCard
			$interactive={interactive}
			data-cy="feature-flag-row"
			onClick={interactive && onOpen ? () => onOpen(row.key) : undefined}
		>
			{/* Purely decorative: it restates the team named in the cell after next, so giving it an
			    accessible name would make every row announce its colour before its team. */}
			<AccentBar aria-hidden="true" data-testid="feature-flag-accent-bar" $colour={teamColour} />
			<FlagName>
				<Text>{row.name}</Text>
			</FlagName>
			<TeamCell>
				<TeamSwatch $colour={teamColour} />
				<Text>{row.teamLabel}</Text>
			</TeamCell>
			<NumberCell>
				<Text>{row.companyCount}</Text>
			</NumberCell>
			<NumberCell>
				<Text>{row.userCount}</Text>
			</NumberCell>
			<RolloutCell>
				{row.isRolledOut ? (
					<>
						<Pill variant={PillVariant.SUCCESS} icon="tick">
							{row.rolledOutDate ? `Rolled out ${row.rolledOutDate}` : 'Rolled out'}
						</Pill>
						<CleanedUpLine>
							<Icon size="s" icon={row.cleanedUp ? 'tick' : 'close'} />
							<Detail>{row.cleanedUp ? 'Cleaned up' : 'Not cleaned up'}</Detail>
						</CleanedUpLine>
					</>
				) : (
					<ProgressRow>
						{/* The gauge is the track, not the fill inside it, so the progressbar role and its
						    values sit here and the fill is left as presentation. */}
						<ProgressTrack
							aria-label="Rollout progress"
							role="progressbar"
							aria-valuenow={row.rolloutPercent}
							aria-valuemin={0}
							aria-valuemax={100}
						>
							<ProgressFill data-testid="feature-flag-rollout-fill" $percent={row.rolloutPercent} />
						</ProgressTrack>
						<PercentLabel>
							<Detail>{row.rolloutPercentLabel}</Detail>
						</PercentLabel>
					</ProgressRow>
				)}
			</RolloutCell>
			<ChevronCell>{interactive ? <Icon size="m" icon="chevronRight" /> : null}</ChevronCell>
		</RowCard>
	);
}
